import { arrayOf, bool, array, shape, string } from "prop-types";
import { getLink } from "@lib/getLink";
import { resolveContentBlocks } from "./lib/resolveContentBlocks";
import { getImages } from "./lib/getImages";
import { SMALL, MED, LRG, LEFT } from "./constants";
import { LinkableArea } from "./LinkableArea";
import styles from "./styles/ContentSection.module.css";

export const ContentSection = ({ section }) => {
  const {
    contentSection: {
      contentBlocks,
      options: {
        backgroundOptions: { bgColor, bgImage },
        footerOptions: { showFooter, caption, footerAlign, ctaTitle, ctaLink },
        spacingOptions: { stackOnMobile, gridGap, padding, height },
        sectionLink,
      },
    },
  } = section;

  const formattedSectionLink = sectionLink ? getLink(sectionLink) : {};
  const formattedCtaLink = ctaLink ? getLink(ctaLink) : {};

  const sectionStyles = `
    ${styles.container}
    ${stackOnMobile ? styles.column : styles.row}
    ${gridGap === SMALL ? styles.gridGapSm : ""}
    ${gridGap === MED ? styles.gridGapMed : ""}
    ${gridGap === LRG ? styles.gridGapLrg : ""}
    ${padding === SMALL ? styles.paddingSm : ""}
    ${padding === MED ? styles.paddingMed : ""}
    ${padding === LRG ? styles.paddingLrg : ""}
    ${height === SMALL ? styles.heightSm : ""}
    ${height === MED ? styles.heightMed : ""}
    ${height === LRG ? styles.heightLrg : ""}
  `;

  const footerStyles = `
    ${styles.footer}
    ${footerAlign === LEFT ? styles.left : ""}
  `;

  const bgImages = bgImage && getImages(bgImage.asset._ref);

  const bgStyle = () => {
    if (bgImage)
      return {
        background: `url(${bgImages.large}) no-repeat`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    if (bgColor) return { background: bgColor };
  };

  const Blocks = () => {
    return (
      <div className={sectionStyles}>
        {contentBlocks?.map((block) => {
          const BlockComponent = resolveContentBlocks(block);
          return <BlockComponent key={block._key} data={block} />;
        })}
      </div>
    );
  };

  const Footer = () => {
    return (
      <div className={footerStyles}>
        {caption && <figcaption>{caption}</figcaption>}
        {ctaTitle && (
          <LinkableArea rawLink={ctaLink} formattedLink={formattedCtaLink}>
            <p>{ctaTitle}</p>
          </LinkableArea>
        )}
      </div>
    );
  };

  const LinkableSection = () => {
    return (
      <LinkableArea
        className={styles.link}
        rawLink={sectionLink}
        formattedLink={formattedSectionLink}
      >
        <div style={bgStyle()}>
          <Blocks />
          {showFooter && <Footer />}
        </div>
      </LinkableArea>
    );
  };

  const Section = () => {
    return (
      <div style={bgStyle()}>
        <Blocks />
        {showFooter && <Footer />}
      </div>
    );
  };

  return (
    <section className={styles.section}>
      {sectionLink && !sectionLink.type && <Section />}
      {sectionLink && sectionLink.type && <LinkableSection />}
    </section>
  );
};

ContentSection.propTypes = {
  section: shape({
    contentBlocks: arrayOf(
      shape({
        _type: string,
        _key: string,
      })
    ),
    options: shape({
      backgroundOptions: shape({
        bgColor: string,
        bgImage: shape({
          _type: string,
          asset: shape({
            _ref: string,
            _type: string,
          }),
        }),
      }),
      spacingOptions: shape({
        stackOnMobile: bool,
        gridGap: string,
        padding: string,
        height: string,
      }),
      footerOptions: shape({
        showFooter: bool,
        footerAlign: string,
        caption: string,
        ctaTitle: string,
        ctaLink: shape({
          refTitle: string,
          type: string,
          handle: string,
          link: shape({
            _type: string,
            blank: bool,
            href: string,
          }),
          slug: shape({
            _type: string,
            current: string,
          }),
          productGroup: array,
        }),
      }),
      sectionLink: shape({
        refTitle: string,
        type: string,
        handle: string,
        link: shape({
          _type: string,
          blank: bool,
          href: string,
        }),
        slug: shape({
          _type: string,
          current: string,
        }),
        productGroup: array,
        document: shape({
          _ref: string,
          _type: string,
        }),
      }),
    }),
  }),
};
